import { SkyButton, SkyDialog, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import { useId, type ComponentProps, type ReactNode } from "react";
import { useLocation } from "wouter";
import type { TransformationGraph, DbtJobView } from "@shared/types/transformations";
import { SingleCheckbox } from "@/components/ui/SingleCheckbox";
import { RepositoryName, RepositoryWithheld } from "@/components/transformations/RepositoryName";
import { InlineErrorBar, type useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { formatInstant } from "@/lib/time/displayTime";
import type { TransformQueue } from "@/lib/transformations/transformQueue";
import { CopyIcon } from "@/lib/icons";
import { useDialogActionDescription } from "@/lib/dialogActionDescription";
import { gitRefTerm } from "@/lib/transformations/repoFileUrl";
import { absolutePath } from "@/lib/routes";
import { usePermissions } from "@/hooks/use-permissions";
import {
  BUNDLE_DETAIL_WITHHELD,
  ASK_ADMIN_DBT_REPOSITORY,
  ASK_ADMIN_DESTINATION,
  DBT_SETTINGS_PATH,
  OPEN_DBT_SETTINGS_LABEL,
  OPEN_WAREHOUSE_SETTINGS_LABEL,
  WAREHOUSE_SETTINGS_PATH,
} from "@/lib/transformations/dbtSettings";
import { runNowOffReason, type NotReadyItem } from "./transformFormatting";
import {
  useChosenModels,
  useIdempotencyKey,
  useRunQueue,
  useRunScope,
  useRunStarter,
  type RunRefusal,
} from "./runDbtJobsState";
import { destinationBlockReason, useDestinationReadiness } from "@/hooks/use-destination-readiness";

/**
 * "Run all dbt jobs now" (canvas 15 block 04; FR-62, API-STANDARD §6, §7b).
 *
 * A transform-only run: no imports, only the transform step and finalize, over
 * the data already in the destination. The dialog says exactly what will run
 * before anything starts — the repository, the runnable dbt jobs in the
 * order the runner merges them, the merged selector, whether tests run, and
 * what is skipped. "Runnable" is the runner's word: enabled AND carrying a
 * selector, because a blank selector is dropped, never widened to the whole
 * project (`runner/steps/transform.ts`). A dialog that said "the whole project"
 * would describe a run the runner refuses.
 *
 * ONE Idempotency-Key per open, reused on every retry from this dialog, so a
 * double click or a flaky network starts one run. The parent mounts the dialog
 * only while it is open, which is what makes "per open" true.
 *
 * A 422 `TRANSFORM_NOT_READY` is the server pre-flighting every reason the
 * runner would skip the step; it arrives as a list of failing checks and is
 * shown as a checklist inside the dialog, each item naming the screen that
 * fixes it. The dialog stays open: the reason is the thing the user has to act
 * on, and a toast would vanish while they read it.
 *
 * Round 6, T1: dbt runs one at a time on an instance (the runner's per-instance
 * slot), so while a transform is in flight this run QUEUES behind it: the bar
 * names the run in progress and the button reads "Queue run". The same
 * selection already waiting is a duplicate the server refuses (409
 * `TRANSFORM_ALREADY_QUEUED`); the dialog says so first and disables the button.
 *
 * Round 6 (T3): with `chosenModels` (the Models view's "Run selected"), the run
 * builds those models only, named by dbt name: the dialog lists them, shows the
 * selection, and asks whether to also build the models they depend on (`+name`,
 * off) and to run their tests (on). No dbt job is part of such a run.
 *
 * Round 6 T-29 (owner: "should we really show this to the user? can we not
 * overcome this?"): one run takes up to `MAX_DBT_SELECT_LENGTH` (100,000)
 * characters and `MAX_CHOSEN_MODELS` (500) names, so there is no counter. Only a
 * choice past either says so, with what to do instead, and cannot start. Every
 * model chosen is a run of those models too (review r1); only when that list is
 * past either limit (`everyModelChosen`) does the parent open this dialog as "Run
 * all dbt jobs now", which sends no body, and the dialog says so.
 */

export interface RunDbtJobsDialogProps {
  onOpenChange: (open: boolean) => void;
  transformations: DbtJobView[];
  /** The binding as it is now; undefined when the graph request has not answered or failed. */
  bundle: TransformationGraph["bundle"] | undefined;
  /**
   * Whether a transform step is still running (the parent's newest row). Round 6: the dialog reads
   * the recent transform steps itself for the run in progress and the waiting ones; this flag
   * still turns the button into "Queue run" while that read is pending.
   */
  runInFlight: boolean;
  /**
   * Session 11 (ruling §8.6): the dbt jobs this run is for, by id. The Tests view's "Run
   * tests" passes the enabled dbt jobs whose Run tests is on, so the run builds their
   * models and then runs their tests. Absent, the run covers every runnable dbt job, as
   * before; present, only the runnable ones it names run and the request names them.
   */
  initialSelection?: readonly string[];
  /**
   * #1293: one dbt job's Run now. The run is that job's selector only: the jobs it depends on are
   * not included, and the dialog says so. Takes the place of `initialSelection`.
   */
  onlyJobId?: string;
  /**
   * Round 6 (T3): the dbt models chosen on the Models view, in name order. Present, the dialog
   * starts a run of exactly these models (`POST /transformations/run` with `models`), never of a
   * dbt job; `initialSelection` is ignored then.
   */
  chosenModels?: readonly string[];
  /**
   * Round 6 T-29: the Models view's "Run selected" with every model chosen, in a project past
   * what one run of chosen models takes (`runSelectedAs`). The dialog is the "Run all dbt jobs
   * now" one (no `chosenModels`), and says why.
   */
  everyModelChosen?: boolean;
  /** #985: the Models view's graph, which holds each model's parents for the "Will run" line. */
  graph?: TransformationGraph;
}

function Fact({ term, children }: { term: string; children: ReactNode }) {
  return (
    <>
      <dt>
        <SkyText size={200} color="muted">
          {term}
        </SkyText>
      </dt>
      <dd>{children}</dd>
    </>
  );
}

function ChecklistItem({ item }: { item: NotReadyItem }) {
  const [, navigate] = useLocation();
  // #1106: the Destination tabs are Instance admin and above. A reader without the key (a
  // Contributor holds `runs.start`) gets who acts, never a button to a screen they cannot open.
  const { canAny } = usePermissions();
  const canOpenDestination = canAny("instance.binding.read");
  return (
    <li className="flex flex-wrap items-baseline gap-x-2" data-testid={`not-ready-${item.field}`}>
      <SkyText size={200} weight="semibold">
        {item.label}:
      </SkyText>
      <SkyText size={200}>{item.issue}</SkyText>
      {item.fixOn === "destination" && !canOpenDestination ? (
        <SkyText size={200} color="muted" testId={`not-ready-${item.field}-ask`}>
          {item.field === "dbtRepoUrl" ? ASK_ADMIN_DBT_REPOSITORY : ASK_ADMIN_DESTINATION}
        </SkyText>
      ) : null}
      {item.fixOn === "destination" && canOpenDestination ? (
        // Round 6 (T2): the repository is on the dbt repository tab, everything else on
        // the Warehouse tab; each link says which.
        <SkyButton
          appearance="subtle"
          size="small"
          onClick={() =>
            navigate(
              absolutePath(
                item.field === "dbtRepoUrl" ? DBT_SETTINGS_PATH : WAREHOUSE_SETTINGS_PATH,
              ),
            )
          }
          testId={`not-ready-${item.field}-fix`}
        >
          {item.field === "dbtRepoUrl" ? OPEN_DBT_SETTINGS_LABEL : OPEN_WAREHOUSE_SETTINGS_LABEL}
        </SkyButton>
      ) : null}
    </li>
  );
}

/** A run of chosen models needs a bound repository and a selection that fits one run. */
function readyToRun({
  repoBound,
  chosen,
  chosenTooLong,
  runnableCount,
}: {
  repoBound: boolean;
  chosen: string[] | null;
  chosenTooLong: boolean;
  runnableCount: number;
}): boolean {
  if (!repoBound) return false;
  return chosen ? chosen.length > 0 && !chosenTooLong : runnableCount > 0;
}

/** The button is live when the destination is Ready, the one job asked for is there, and the run is ready. */
function readyToStart(destinationReady: boolean, jobGone: boolean, runReady: boolean): boolean {
  if (!destinationReady || jobGone) return false;
  return runReady;
}

function runLabel(starting: boolean, queues: boolean): string {
  if (starting) return "Starting…";
  return queues ? "Queue run" : "Run now";
}

/** The server's refusal: what is not ready, each with where to fix it, and the reference id. */
function RefusalBar({ refusal }: { refusal: RunRefusal }) {
  return (
    <SkyMessageBar
      intent="error"
      layout="multiline"
      title="Nothing to run yet"
      testId="run-transformations-refused"
      message={
        <div className="flex flex-col gap-2">
          <SkyText size={200}>{refusal.message}</SkyText>
          <ul className="list-disc space-y-1 pl-5" data-testid="not-ready-checklist">
            {refusal.checklist.map((item) => (
              <ChecklistItem key={item.field} item={item} />
            ))}
          </ul>
          {refusal.reference ? (
            <span className="flex flex-wrap items-center gap-2">
              <SkyText size={200} color="muted">
                Reference id {refusal.reference}
              </SkyText>
              <SkyButton
                appearance="subtle"
                size="small"
                icon={CopyIcon}
                testId="run-transformations-copy-reference"
                onClick={() => {
                  void navigator.clipboard?.writeText(refusal.reference ?? "").catch(() => {
                    /* insecure origin — the id is on screen to be typed */
                  });
                }}
              >
                Copy reference
              </SkyButton>
            </span>
          ) : null}
        </div>
      }
    />
  );
}

/** Why the run cannot start, or what it will run instead, before it is asked for. */
function ReadinessNotices({
  refused,
  everyModelChosen,
  chosen,
  bindingUnread,
  repoBound,
  enabledCount,
  runnableCount,
  onOpenDbtSettings,
}: {
  refused: boolean;
  everyModelChosen: boolean;
  chosen: boolean;
  bindingUnread: boolean;
  repoBound: boolean;
  enabledCount: number;
  runnableCount: number;
  /** Absent for a reader without `instance.binding.read` (#1106). */
  onOpenDbtSettings: (() => void) | undefined;
}) {
  return (
    <>
      {everyModelChosen && !chosen ? (
        <SkyMessageBar
          intent="info"
          layout="multiline"
          message="Every model is chosen, and that is more models than one run takes. This run is Run all dbt jobs now instead: it runs every enabled dbt job, so it builds the models their selectors pick."
          testId="run-transformations-every-model"
        />
      ) : null}

      {!refused && bindingUnread ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title="The binding could not be read"
          message="Whether a dbt repository is bound is not known right now. Close this dialog, retry the binding on the page, then run again."
          testId="run-transformations-binding-unread"
        />
      ) : null}

      {!refused && !bindingUnread && !repoBound ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title="No dbt repository is bound"
          message="Nothing can run until an instance admin or above binds a repository on the Destination screen's dbt repository tab."
          testId="run-transformations-unbound"
          actions={
            // #1106: the message already says who acts. The button only for a reader who can
            // open the dbt repository tab.
            onOpenDbtSettings ? (
              <SkyButton
                appearance="secondary"
                size="small"
                testId="run-transformations-open-destination"
                // Straight to the dbt repository tab the message names (Session 10 P5).
                onClick={onOpenDbtSettings}
              >
                {OPEN_DBT_SETTINGS_LABEL}
              </SkyButton>
            ) : undefined
          }
        />
      ) : null}

      {!refused && !chosen && repoBound && enabledCount === 0 ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title="No enabled dbt job exists"
          message="Enable one, or create one, then try again."
          testId="run-transformations-none-enabled"
        />
      ) : null}

      {!refused && !chosen && repoBound && enabledCount > 0 && runnableCount === 0 ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title="No enabled dbt job has a selector"
          message="A dbt job with no selector is left out of the run. The runner never widens a run to the whole project. Give one a selector, then try again."
          testId="run-transformations-no-selector"
        />
      ) : null}
    </>
  );
}

/** What the run reads: the repository and its ref, then the chosen models or the dbt jobs. */
function RunFacts({
  bundle,
  chosen,
  willRun,
  chosenSelect,
  runnable,
  enabledCount,
  mergedSelector,
  withTests,
  skipped,
  onlyJob,
  dependencies,
}: {
  /** The binding, only while a repository is bound. */
  bundle: TransformationGraph["bundle"] | undefined;
  chosen: string[] | null;
  /** The "Will run" sentence of a run of chosen models (#985). */
  willRun: string;
  chosenSelect: string;
  runnable: DbtJobView[];
  enabledCount: number;
  mergedSelector: string;
  withTests: DbtJobView[];
  skipped: { notSelected: DbtJobView[]; disabled: DbtJobView[]; withoutSelector: DbtJobView[] };
  onlyJob: DbtJobView | undefined;
  dependencies: DbtJobView[];
}) {
  return (
    <dl className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-4 gap-y-2">
      {/* Round 7 T-38: owner/repo as a link, then its branch on a line of its own (it read
          "https://github.com/... · main"). */}
      <Fact term="Repository">
        {bundle?.repoUrl ? (
          <RepositoryName repoUrl={bundle.repoUrl} />
        ) : bundle?.repoBound && bundle.repoUrlWithheld ? (
          <RepositoryWithheld testId="run-transformations-repository-withheld" />
        ) : (
          <SkyText size={200} color="muted">
            Not set
          </SkyText>
        )}
      </Fact>
      {bundle?.repoUrl || (bundle?.repoBound && bundle.repoUrlWithheld) ? (
        bundle.ref ? (
          <Fact term={gitRefTerm(bundle.ref)}>
            <SkyText size={200} font="monospace" block className="break-all">
              {bundle.ref}
            </SkyText>
          </Fact>
        ) : bundle.detailsWithheld ? (
          // #1362: the branch is Instance admin and above, as on the Bundle tab and in Details.
          <Fact term="Branch">
            <SkyText size={200} color="muted" block testId="run-transformations-branch-withheld">
              {BUNDLE_DETAIL_WITHHELD}
            </SkyText>
          </Fact>
        ) : null
      ) : null}
      {chosen ? (
        <>
          <Fact term="Will run">
            <SkyText size={200} block testId="run-transformations-will-run">
              {willRun}
            </SkyText>
          </Fact>
          <Fact term="Selection">
            <SkyText
              size={200}
              font="monospace"
              block
              className="break-all"
              testId="run-transformations-selection"
            >
              {chosenSelect}
            </SkyText>
          </Fact>
        </>
      ) : null}
      {chosen ? null : (
        <JobsFacts
          runnable={runnable}
          enabledCount={enabledCount}
          mergedSelector={mergedSelector}
          withTests={withTests}
          skipped={skipped}
          onlyJob={onlyJob}
          dependencies={dependencies}
        />
      )}
    </dl>
  );
}

/** Will run: the dbt jobs in dependency order, or why none would. */
function willRunText(
  runnable: DbtJobView[],
  enabledCount: number,
  onlyJob: DbtJobView | undefined,
  canWrite: boolean,
): string {
  if (onlyJob) {
    // One job's own Run now: say why this job cannot run, never the instance-wide "none enabled".
    return runnable.length > 0 ? onlyJob.name : (runNowOffReason(onlyJob, canWrite) ?? "None");
  }
  if (runnable.length > 0) {
    return `${runnable.map((row) => row.name).join(" → ")} (${runnable.length} enabled, in dependency order)`;
  }
  return enabledCount === 0 ? "None enabled" : "None: no enabled dbt job has a selector";
}

/** Tests: off for every job that runs, on for all of them, or the jobs that run them. */
function testsText(withTests: DbtJobView[], runnable: DbtJobView[], oneJob: boolean): string {
  if (withTests.length === 0) {
    return oneJob
      ? "No (Run tests is off for this dbt job)"
      : "No (Run tests is off for every dbt job that runs)";
  }
  // #1293: one dbt job (its own Run now) reads a plain yes, never "for all 1".
  if (withTests.length === runnable.length) {
    return runnable.length === 1 ? "Yes" : `Yes, for all ${runnable.length}`;
  }
  return `Yes, for ${withTests.map((row) => row.name).join(", ")}`;
}

/** The dialog's question: the chosen models, one dbt job (#1293), or every enabled one. */
function dialogTitle(
  chosen: boolean,
  onlyJob: DbtJobView | undefined,
  oneJobAsked: boolean,
): string {
  if (chosen) return "Run chosen models now?";
  if (oneJobAsked) return onlyJob ? `Run ${onlyJob.name} now?` : "Run this dbt job now?";
  return "Run all dbt jobs now?";
}

function dialogDescription(
  chosen: boolean,
  onlyJob: DbtJobView | undefined,
  hasDependencies: boolean,
  oneJobAsked: boolean,
): string {
  if (chosen) {
    return "Starts a run with no imports that builds only the models chosen on the Models view. It uses the data already in the destination.";
  }
  if (oneJobAsked && !onlyJob) return ONE_JOB_GONE;
  if (onlyJob) {
    // The dependency sentence only where there is something left out (a root job has none).
    const leftOut = hasDependencies ? " Jobs it depends on are not included." : "";
    return `Starts a run with no imports that runs only ${onlyJob.name}. It uses the data already in the destination.${leftOut}`;
  }
  return "Starts a run with no imports that runs every enabled dbt job. It uses the data already in the destination.";
}

/** #1293: the dialog was opened for one job and the list no longer has it (deleted meanwhile). */
const ONE_JOB_GONE = "This dbt job no longer exists.";

/** The dbt jobs a run of every enabled job runs, its merged selector, its tests and what it skips. */
function JobsFacts({
  runnable,
  enabledCount,
  mergedSelector,
  withTests,
  skipped,
  onlyJob,
  dependencies,
}: {
  runnable: DbtJobView[];
  enabledCount: number;
  mergedSelector: string;
  withTests: DbtJobView[];
  skipped: { notSelected: DbtJobView[]; disabled: DbtJobView[]; withoutSelector: DbtJobView[] };
  onlyJob: DbtJobView | undefined;
  dependencies: DbtJobView[];
}) {
  const canWrite = usePermissions().canAny("dbt_jobs.write");
  const skippedCount =
    skipped.disabled.length + skipped.withoutSelector.length + skipped.notSelected.length;
  return (
    <>
      <Fact term="Will run">
        <SkyText size={200} block testId="run-transformations-will-run">
          {willRunText(runnable, enabledCount, onlyJob, canWrite)}
        </SkyText>
      </Fact>
      {dependencies.length > 0 ? (
        <Fact term="Depends on">
          <SkyText size={200} block testId="run-transformations-depends-on">
            {dependencies.map((row) => row.name).join(", ")}. Not part of this run.
          </SkyText>
        </Fact>
      ) : null}
      <Fact term="Selection">
        {runnable.length === 0 ? (
          <SkyText size={200} color="muted">
            —
          </SkyText>
        ) : (
          <SkyText size={200} font="monospace" block className="break-all">
            {mergedSelector}
          </SkyText>
        )}
      </Fact>
      <Fact term="Tests">
        <SkyText size={200} block testId="run-transformations-tests">
          {testsText(withTests, runnable, onlyJob !== undefined)}
        </SkyText>
      </Fact>
      {/* #1293: a run of one dbt job names what it leaves out on Depends on; the other jobs
          are not part of the question, so they are not listed as skipped. */}
      {onlyJob ? null : <SkippedFact skipped={skipped} skippedCount={skippedCount} />}
    </>
  );
}

/** Skipped: the dbt jobs a run of every enabled job, or of a selection, leaves out. */
function SkippedFact({
  skipped,
  skippedCount,
}: {
  skipped: { notSelected: DbtJobView[]; disabled: DbtJobView[]; withoutSelector: DbtJobView[] };
  skippedCount: number;
}) {
  return (
    <>
      <Fact term="Skipped">
        <SkyText
          size={200}
          block
          color={skippedCount === 0 ? "muted" : "default"}
          testId="run-transformations-skipped"
        >
          {skippedCount === 0
            ? "—"
            : [
                skipped.notSelected.length > 0
                  ? `${skipped.notSelected.map((row) => row.name).join(", ")} (not in this run)`
                  : null,
                skipped.disabled.length > 0
                  ? `${skipped.disabled.map((row) => row.name).join(", ")} (disabled)`
                  : null,
                skipped.withoutSelector.length > 0
                  ? `${skipped.withoutSelector.map((row) => row.name).join(", ")} (no selector)`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
        </SkyText>
      </Fact>
    </>
  );
}

/** The same run already waiting (the button is off and this says why), or that this run queues. */
function QueueNotice({
  queue,
  queues,
  chosen,
  zone,
  duplicateReasonId,
}: {
  queue: TransformQueue;
  queues: boolean;
  chosen: boolean;
  zone: string;
  duplicateReasonId: string;
}) {
  if (queue.duplicateRunId) {
    return (
      <div id={duplicateReasonId}>
        <SkyMessageBar
          intent="info"
          layout="multiline"
          title="This run is already waiting"
          message={`The same ${chosen ? "chosen models" : "dbt jobs"} are already waiting in run ${queue.duplicateRunId}.`}
          testId="run-transformations-duplicate"
        />
      </div>
    );
  }
  if (!queues) return null;
  const holder = queue.holder;
  const ahead = queue.waiting;
  const oneTime =
    "dbt runs one at a time on an instance, so two runs never build the same models at once.";
  // Before or without a read, the count is unknown (0), so it reads in the plural.
  let title = `This run waits for the ${ahead === 1 ? "run" : "runs"} ahead of it`;
  let message = `This run starts when the runs ahead of it finish. ${oneTime}`;
  if (holder) {
    const transforming = `Run ${holder.runId} is transforming now (started ${formatInstant(
      holder.startedAt,
      zone,
    )}).`;
    // #1419: the same runs the banner names, the running one and the ones waiting before this.
    if (ahead > 0) {
      title = "This run waits for the runs ahead of it";
      message = `${transforming} ${ahead === 1 ? "One other run is" : `${ahead} other runs are`} waiting. This run starts after them. ${oneTime}`;
    } else {
      title = "This run waits for the one in progress";
      message = `${transforming} This run starts when it finishes. ${oneTime}`;
    }
  } else if (ahead > 0) {
    message = `${ahead === 1 ? "One run is" : `${ahead} runs are`} waiting for dbt. This run starts after ${ahead === 1 ? "it" : "them"}. ${oneTime}`;
  }
  return (
    <SkyMessageBar
      intent="info"
      layout="multiline"
      title={title}
      message={message}
      testId="run-transformations-in-flight"
    />
  );
}

/** Round 6 (T3): the two choices of a run of chosen models. */
function ChosenOptions({
  buildParents,
  runTests,
  onBuildParents,
  onRunTests,
}: {
  buildParents: boolean;
  runTests: boolean;
  onBuildParents: (value: boolean) => void;
  onRunTests: (value: boolean) => void;
}) {
  return (
    <div className="flex flex-col gap-2" data-testid="run-transformations-chosen-options">
      <SingleCheckbox
        label="Also build the models they depend on"
        description="Adds + before each model, so dbt builds what they read from first."
        checked={buildParents}
        onChange={onBuildParents}
        testId="run-transformations-build-parents"
      />
      <SingleCheckbox
        label="Run their tests"
        description="Runs dbt test on the chosen models, after the run builds."
        checked={runTests}
        onChange={onRunTests}
        testId="run-transformations-run-tests"
      />
    </div>
  );
}

/** What the run reads and chooses: the facts, a chosen-models run's options, and its limit. */
function RunDetails({
  bundle,
  scope,
  choice,
  onBuildParents,
  onRunTests,
}: {
  bundle: TransformationGraph["bundle"] | undefined;
  scope: ReturnType<typeof useRunScope>;
  choice: ReturnType<typeof useChosenModels>;
  onBuildParents: (value: boolean) => void;
  onRunTests: (value: boolean) => void;
}) {
  const { chosen, chosenTooLong } = choice;
  return (
    <>
      {scope.oneJobAsked && !scope.onlyJob ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          message={ONE_JOB_GONE}
          testId="run-transformations-job-gone"
        />
      ) : (
        <RunFacts
          bundle={bundle}
          chosen={chosen}
          willRun={choice.willRun}
          chosenSelect={choice.chosenSelect}
          runnable={scope.runnable}
          enabledCount={scope.enabled.length}
          mergedSelector={scope.mergedSelector}
          withTests={scope.withTests}
          skipped={{
            notSelected: scope.notSelected,
            disabled: scope.disabled,
            withoutSelector: scope.withoutSelector,
          }}
          onlyJob={scope.onlyJob}
          dependencies={scope.dependencies}
        />
      )}

      {chosen ? (
        <ChosenOptions
          buildParents={choice.buildParents}
          runTests={choice.runTests}
          onBuildParents={onBuildParents}
          onRunTests={onRunTests}
        />
      ) : null}

      {chosenTooLong ? (
        <SkyMessageBar
          intent="error"
          layout="multiline"
          message="Too many models chosen. Choose fewer, or run a dbt job that selects them by tag."
          testId="run-transformations-too-many"
        />
      ) : null}
    </>
  );
}

/** Why the run did not start or cannot start: the failure, the server's refusal, the readiness. */
function RunNotices({
  failure,
  refusal,
  destinationReason,
  readiness,
}: {
  failure: ReturnType<typeof useInlineError>;
  refusal: RunRefusal | null;
  destinationReason: string | null;
  readiness: ComponentProps<typeof ReadinessNotices>;
}) {
  return (
    <>
      <InlineErrorBar
        error={failure.error}
        title={failure.error?.retryable ? undefined : "The run could not be started"}
        testId="run-transformations-error"
      />

      {refusal ? <RefusalBar refusal={refusal} /> : null}

      {destinationReason ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          message={destinationReason}
          testId="run-transformations-destination"
        />
      ) : null}

      <ReadinessNotices {...readiness} />
    </>
  );
}

export function RunDbtJobsDialog({
  onOpenChange,
  transformations,
  bundle,
  runInFlight,
  initialSelection,
  onlyJobId,
  chosenModels,
  everyModelChosen = false,
  graph,
}: RunDbtJobsDialogProps) {
  const [, navigate] = useLocation();
  const canOpenDestination = usePermissions().canAny("instance.binding.read");
  const zone = useDisplayTz();
  const { idempotencyKey, rekey } = useIdempotencyKey();
  const scope = useRunScope(transformations, onlyJobId, initialSelection);
  const { onlyJob, dependencies, enabled, runnable, oneJobAsked } = scope;
  const choice = useChosenModels(chosenModels, graph);
  const { chosen, chosenTooLong } = choice;
  // T1: the recent transform steps, for the run holding the instance's dbt slot and the waiters.
  const { queue, queues, queuedBehind } = useRunQueue({
    chosenSelect: chosen ? choice.chosenSelect : null,
    requestIds: scope.requestIds,
    runInFlight,
  });

  // `bundle` undefined is "the binding could not be read", which is not the
  // same claim as "no repository is bound": the first is retried, the second
  // is fixed on the Destination screen.
  const repoBound = bundle?.repoBound ?? false;
  // Round 7 (backlog "Name the reason on the Run dialog's disabled Queue run button"): the
  // disabled button is described by the bar that says why.
  const duplicateReasonId = useId();
  useDialogActionDescription(
    "run-transformations-dialog",
    "primary",
    queue.duplicateRunId ? duplicateReasonId : null,
  );
  const changeOption = (set: (value: boolean) => void) => (value: boolean) => {
    set(value);
    rekey();
  };
  // #375, #923: the server refuses every run while the destination is not Ready.
  const destinationReason = destinationBlockReason(useDestinationReadiness());
  const ready = readyToStart(
    destinationReason === null,
    oneJobAsked && !onlyJob,
    readyToRun({ repoBound, chosen, chosenTooLong, runnableCount: runnable.length }),
  );
  const starter = useRunStarter({
    chosen,
    buildParents: choice.buildParents,
    runTests: choice.runTests,
    selectedIds: scope.hasSelection ? runnable.map((row) => row.id) : null,
    idempotencyKey,
    queuedBehind,
    onOpenChange,
  });
  const { refusal } = starter;

  return (
    <SkyDialog
      open
      onOpenChange={onOpenChange}
      title={dialogTitle(chosen !== null, onlyJob, oneJobAsked)}
      description={dialogDescription(
        chosen !== null,
        onlyJob,
        dependencies.length > 0,
        oneJobAsked,
      )}
      size="medium"
      testId="run-transformations-dialog"
      // The dialog closes itself after a primary action resolves; a refusal has
      // to stay on screen, so the close on success is explicit (`onOpenChange`
      // above) and the automatic one is declined.
      onBeforeClose={(reason) => reason !== "primaryAction"}
      primaryAction={{
        label: runLabel(starter.isPending, queues),
        onClick: starter.run,
        disabled: !ready || starter.isPending || queue.duplicateRunId !== null,
        loading: starter.isPending,
      }}
      secondaryAction={{ label: refusal ? "Close" : "Cancel", onClick: () => onOpenChange(false) }}
    >
      <div className="flex flex-col gap-4">
        <RunNotices
          failure={starter.failure}
          refusal={refusal}
          destinationReason={destinationReason}
          readiness={{
            refused: refusal !== null,
            everyModelChosen,
            chosen: chosen !== null || onlyJobId !== undefined,
            bindingUnread: bundle === undefined,
            repoBound,
            enabledCount: enabled.length,
            runnableCount: runnable.length,
            onOpenDbtSettings: canOpenDestination
              ? () => navigate(absolutePath(DBT_SETTINGS_PATH))
              : undefined,
          }}
        />

        <RunDetails
          bundle={repoBound ? bundle : undefined}
          scope={scope}
          choice={choice}
          onBuildParents={changeOption(choice.setBuildParents)}
          onRunTests={changeOption(choice.setRunTests)}
        />

        <QueueNotice
          queue={queue}
          queues={queues}
          chosen={chosen !== null}
          zone={zone}
          duplicateReasonId={duplicateReasonId}
        />
      </div>
    </SkyDialog>
  );
}
